import { parseChatbotBody } from '../models/chatbotBody'
import type { ChatResponseDto } from '../models/orpheus.dto'
import type { ChatMessage } from '../models/orpheus.types'

import {
  firstReportPayload,
  mapMessageSource,
  toMessageCredits,
  type MessageIdentity,
} from './messageMapper'
import { normalizeClarification } from './orpheusClarification'
import { extractSetupParts } from './orpheusSetupParts'

/** Token-scoped legacy string decoder; malformed bodies return null without retaining raw content. */
export function parseResponseData<T>(body: unknown): T | null {
  return parseChatbotBody<T>(body)
}

/** Map one terminal blocking response without generating turn identity inside the service. */
export function mapResponse(response: ChatResponseDto, identity: MessageIdentity): ChatMessage {
  const metadata = response.metadata
  const body = response.response
  const controllerParams = response.controller_params ?? metadata?.controller_params ?? null
  const messageId = response.message_id ?? response.msg_id ?? body.msg_id ?? null

  return mapMessageSource(
    {
      role: 'assistant',
      content: typeof body.content === 'string' ? body.content : '',
      response: body,
      responseType: body.type ?? null,
      messageId,
      createdAt: response.created_at ?? metadata?.created_at ?? null,
      updatedAt: response.updated_at ?? metadata?.updated_at ?? null,
      controllerParams,
      payload: firstReportPayload(
        body.raw_data,
        metadata?.controller_response,
        metadata?.response?.raw_data,
      ),
      recommendations: body.recommendations ?? [],
      recommendationData: body.recommendation_data ?? null,
      responseBlocks:
        body.response_blocks ??
        body.responseBlocks ??
        response.response_blocks ??
        response.responseBlocks ??
        metadata?.response?.response_blocks ??
        metadata?.response?.responseBlocks ??
        [],
      followups: (body.followups ?? response.followups ?? []).filter(
        (item): item is string => typeof item === 'string',
      ),
      suggestedPrompts:
        body.suggested_prompts ?? response.suggested_prompts ?? metadata?.suggested_prompts ?? [],
      channels: [],
      extractedParameters: metadata?.extracted_parameters,
      dateRangeCandidate: metadata?.resolved_date_range,
      setupParts: extractSetupParts(response),
      clarification: normalizeClarification(body, metadata),
      // Kept on the message itself, not only folded into the balance, so each answer keeps
      // showing what IT cost whatever a later message spends (v3 ORP-24542).
      credits: toMessageCredits(response.credits),
    },
    identity,
  )
}
